/**
 * enrolmentFee — what ONE student pays for ONE course. The twin of
 * vc_be/utils/enrolmentFee.js: the student form previews with these rules and
 * the server re-prices with its own copy on save, so the payable shown is the
 * payable stored. Both test files pin the same cases — change both.
 *
 * An enrolment is priced at a fee before discount (the course's own fee unless
 * the admin typed another), less this student's discount (₹ or %). What is left
 * is `totalFees` — the figure every fee screen, EMI plan and payment reads.
 * A discount belongs to the enrolment, so it never reaches another student in
 * the same course or batch.
 */
import { formatCurrency } from "./format";

export const MAX_FEE = 10_000_000;
export const MAX_REASON = 200;
export const DISCOUNT_MODES = ["amount", "percent"];
const EPSILON = 0.005;

const round2 = (n) => Math.round(n * 100) / 100;
const sameMoney = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) < EPSILON;
// The server words its messages this way; keep the two identical.
const rupees = (n) => `₹${round2(Number(n) || 0).toLocaleString("en-IN")}`;

/** A typed money figure: a number, null for blank, NaN for anything else. */
export function readMoney(raw) {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === "number") return raw;
  const s = String(raw).replace(/[,\s₹]/g, "");
  return s === "" ? null : Number(s);
}

/**
 * The rupees in a course's typed fee, or null when it names none.
 * "50000", "50,000", "₹50,000", "Rs. 50,000", "50,000/-". The FIRST number
 * wins, so "Rs. 50,000" is 50000 and not the half-rupee a strip-everything
 * parse makes of it.
 */
export function parseFeeText(raw) {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === "number") return Number.isFinite(raw) && raw >= 0 ? raw : null;
  const m = String(raw).replace(/,/g, "").match(/\d+(?:\.\d+)?/);
  return m ? Number(m[0]) : null;
}

/**
 * What a course costs before anything is taken off — the figure the student
 * form fills in when a course is picked, and the same one self-enrolment
 * charges: a self-paced course's sale price, else its price; a batch course's
 * typed fee plus a registration fee charged ON TOP of it (one included in the
 * fee is already in it). null when a batch course names no fee.
 * Twin of vc_be utils/websiteCheckoutRules.batchCourseTotal.
 */
export function courseListFee(course) {
  if (!course) return null;
  if (course.courseType === "self-paced") return Number(course.discountPrice || course.price) || 0;
  const fee = parseFeeText(course.fees);
  if (fee === null) return null;
  const reg = course.registrationFee;
  const extra = reg && reg.enabled === true && reg.includedInFee === false ? Number(reg.amount) || 0 : 0;
  return Math.round((fee + Math.max(extra, 0)) * 100) / 100;
}

/**
 * Price one enrolment. Same contract as the server's.
 * @returns {{ value: { courseFee, discount, totalFees } } | { error: string, field: string }}
 */
export function priceEnrolment({ courseFee, discount } = {}) {
  const typed = readMoney(courseFee);
  const fee = typed === null ? 0 : typed;
  if (!Number.isFinite(fee) || fee < 0) {
    return { error: "The course fee must be a number, 0 or more.", field: "courseFee" };
  }
  if (fee > MAX_FEE) return { error: "The course fee is too large. Check the amount.", field: "courseFee" };
  const base = round2(fee);

  const value = readMoney(discount?.value);
  if (value === null || value === 0) {
    return { value: { courseFee: base, discount: null, totalFees: base } };
  }
  if (!Number.isFinite(value) || value < 0) {
    return { error: "The discount must be a number, 0 or more.", field: "discountValue" };
  }
  const mode = discount?.mode || "amount";
  if (!DISCOUNT_MODES.includes(mode)) {
    return { error: "Choose ₹ or % for the discount.", field: "discountValue" };
  }
  if (base === 0) {
    return { error: "There is no fee to discount. Enter the course fee first.", field: "discountValue" };
  }

  let amount;
  if (mode === "percent") {
    if (value > 100) return { error: "A discount can be at most 100%.", field: "discountValue" };
    // Whole rupees: fees are quoted and collected in rupees, so 12.5% of
    // ₹19,999 is ₹2,500 off, not ₹2,499.88. A ₹ discount is kept as typed.
    amount = Math.round((base * value) / 100);
  } else {
    if (value > base + EPSILON) {
      return { error: `The discount can't be more than the course fee (${rupees(base)}).`, field: "discountValue" };
    }
    amount = round2(value);
  }

  const reason = typeof discount?.reason === "string" ? discount.reason.trim() : "";
  if (reason.length > MAX_REASON) {
    return { error: `Keep the discount reason under ${MAX_REASON} characters.`, field: "discountReason" };
  }

  return {
    value: {
      courseFee: base,
      discount: { mode, value: round2(value), amount, reason },
      totalFees: round2(Math.max(base - amount, 0)),
    },
  };
}

/**
 * The server's write rule: `stored` is the enrolment as saved (`totalFees`,
 * `paidFees`), or null for a new one. A payable below what has been paid is
 * refused — only when the payable changed, so an overpaid row from before this
 * rule still re-saves.
 */
export function planEnrolmentFee({ input, stored = null, label = "This course" } = {}) {
  let priced;
  if (input && Object.prototype.hasOwnProperty.call(input, "courseFee")) {
    const result = priceEnrolment({ courseFee: input.courseFee, discount: input.discount });
    if (result.error) return { error: `${label}: ${result.error}`, field: result.field };
    priced = result.value;
  } else {
    const typed = readMoney(input?.totalFees);
    const total = typed === null || !Number.isFinite(typed) || typed < 0 ? 0 : round2(typed);
    if (stored && sameMoney(stored.totalFees, total)) return { set: { totalFees: total } };
    priced = { courseFee: total, discount: null, totalFees: total };
  }

  if (stored && !sameMoney(stored.totalFees, priced.totalFees)) {
    const paid = Number(stored.paidFees) || 0;
    if (priced.totalFees < paid - EPSILON) {
      return {
        error: `${label}: ${rupees(paid)} has already been paid, so the fee after discount can't be less than that. `
          + "Lower the discount, or record a refund in Fees first.",
        field: "discountValue",
      };
    }
  }

  return { set: priced };
}

/* ── The form's editor state ──────────────────────────────────────────────── */

// What the inputs hold: strings, as typed. `courseFee` blank is a free course.
export const EMPTY_FEE = Object.freeze({ courseFee: "", discountMode: "amount", discountValue: "", discountReason: "" });

const asText = (n) => (n === undefined || n === null || n === "" ? "" : String(n));

/** A course was just picked: its own fee, no discount. */
export const feeFromCourse = (listFee) => ({ ...EMPTY_FEE, courseFee: asText(listFee) });

/**
 * An enrolment as saved. A row from before discounts has only `totalFees`,
 * which is then the fee with nothing off — exactly what it always meant.
 */
export const feeFromEnrolment = (ca) => ({
  courseFee: asText(ca?.courseFee ?? ca?.totalFees),
  discountMode: DISCOUNT_MODES.includes(ca?.discount?.mode) ? ca.discount.mode : "amount",
  discountValue: ca?.discount ? asText(ca.discount.value) : "",
  discountReason: ca?.discount?.reason || "",
});

/** The body's half for one course — the server prices it again from this. */
export const feePayload = (state) => ({
  courseFee: state?.courseFee ?? "",
  discount: {
    mode: state?.discountMode || "amount",
    value: state?.discountValue ?? "",
    reason: state?.discountReason ?? "",
  },
});

/** Price the editor state, against the saved enrolment when there is one. */
export const priceFeeState = (state, stored = null, label) => (
  planEnrolmentFee({ input: feePayload(state), stored, label })
);

/** "10% off (₹2,500) · Sibling", "₹2,500 off". Empty for no discount. */
export function describeDiscount(discount) {
  const amount = Number(discount?.amount) || 0;
  if (amount <= 0) return "";
  const off = discount.mode === "percent"
    ? `${discount.value}% off (${formatCurrency(amount)})`
    : `${formatCurrency(amount)} off`;
  return discount.reason ? `${off} · ${discount.reason}` : off;
}
